'use client';

import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';

import { logger } from '@/lib/logger';

import { FeatureFlagsContext } from '@/components/FeatureFlags/FeatureFlagsProvider';

import {
  DEFAULT_FEATURE_FLAGS,
  SUPPORTED_FEATURE_FLAGS,
  type FeatureFlagValues,
} from '@/utils/featureFlags';

/**
 * Resolves a flag from the URL query and cookie, persisting the toggle the
 * same way `useFeature` does. Returns `undefined` when there is no explicit
 * signal or outside the browser.
 */
export function resolveFlagFromQueryAndCookie(flag: string): boolean | undefined {
  if (typeof window === 'undefined') return undefined;

  const { search } = window.location;
  const enableFeature = search?.includes(`${flag}=true`);
  const disableFeature = search?.includes(`${flag}=false`);
  const toggleOnPersisted = Cookies.get(flag) === 'true';

  if (disableFeature) {
    Cookies.remove(flag);
    return false;
  }
  if (enableFeature || toggleOnPersisted) {
    Cookies.set(flag, 'true', { expires: 14 });
    return true;
  }
  return undefined;
}

/** Server-managed flags come from page props; other flags retain query/cookie evaluation. */
export function useFeature(flag: string) {
  const flags = useContext(FeatureFlagsContext);
  const { asPath } = useRouter();
  const [featureEnabled, setFeatureEnabled] = useState<boolean>(false);

  useEffect(() => {
    if (!SUPPORTED_FEATURE_FLAGS.includes(flag)) {
      logger.warn(`Unsupported feature flag: "${flag}"`);
      return;
    }

    const resolved = resolveFlagFromQueryAndCookie(flag);
    if (resolved != null) {
      setFeatureEnabled(resolved);
    }
  }, [flag, asPath]);

  return Object.hasOwn(DEFAULT_FEATURE_FLAGS, flag)
    ? flags[flag as keyof FeatureFlagValues]
    : featureEnabled;
}

/**
 * Enabled feature flags list.
 * @returns Feature flags names.
 */
export const useEnabledFeatures = () => {
  const { asPath } = useRouter();
  const [flags, setFlags] = useState<string[]>([]);

  useEffect(() => {
    setFlags(SUPPORTED_FEATURE_FLAGS.filter((feature) => resolveFlagFromQueryAndCookie(feature)));
  }, [asPath]);

  return flags;
};

/**
 * Reset all feature flags.
 */
export const resetAllFeatureFlags = () =>
  SUPPORTED_FEATURE_FLAGS.forEach((feature) => {
    Cookies.remove(feature);
  });
